import {
  alpha,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Skeleton
} from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/material/styles';
import CloseButton from 'src/components/CloseButton';
import { useNotifications } from 'src/contexts/NotificationContext';

import { formatDistance } from 'date-fns';

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
      background-color: ${theme.palette.error.main};
      color: #fff;
      min-width: 16px;
      height: 16px;
      padding: 0;
      box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
    }
  `
);

function HeaderNotifications() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const { notifications, markAsRead, markAllAsRead, getUnreadCount } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => setFirstLoad(false), 400);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleMarkAllAsRead = (): void => {
    markAllAsRead();
    setOpen(false);
  };

  const handleMarkAsRead = (id: string): void => {
    markAsRead(id);
  };

  const unreadCount = getUnreadCount();

  const getSeverityIcon = (severity: 'error' | 'warning' | 'info' | 'success') => {
    switch (severity) {
      case 'error':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'warning':
        return <WarningIcon fontSize="small" color="warning" />;
      default:
        return null;
    }
  };

  return (
    <>
      <Tooltip arrow title="Notifications">
        <IconButton color="primary" ref={ref} onClick={handleOpen} sx={{ width: 44, height: 44 }} aria-label="open notifications">
          <NotificationsBadge
            badgeContent={unreadCount}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>
      {!isMobile ? (
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: { minWidth: 320, p: 0 }
        }}
        disableScrollLock
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            pb: 1
          }}
        >
          <Typography variant="h6">Notifications</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={handleMarkAllAsRead}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  minWidth: 'auto'
                }}
              >
                Mark all as read
              </Button>
            )}
            <CloseButton
              aria-label="close"
              onClick={handleClose}
            >
              <CloseIcon />
            </CloseButton>
          </Box>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          {firstLoad ? (
            Array.from({ length: 4 }).map((_, i) => (
              <ListItem key={i} sx={{ p: 2, minWidth: 350 }}>
                <Box display="flex" alignItems="flex-start" gap={1} width="100%">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Box flex={1}>
                    <Skeleton width="40%" />
                    <Skeleton width="90%" />
                  </Box>
                </Box>
              </ListItem>
            ))
          ) : notifications.length > 0 ? (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{ 
                  p: 2, 
                  minWidth: 350, 
                  display: 'block',
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  cursor: 'pointer'
                }}
                onClick={() => handleMarkAsRead(notification.id)}
              >
                <Box display="flex" alignItems="flex-start" gap={1}>
                  {getSeverityIcon(notification.severity)}
                  <Box flex="1">
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                      <Chip 
                        label={notification.severity.toUpperCase()} 
                        size="small" 
                        color={notification.severity === 'error' ? 'error' : 'warning'}
                        variant="outlined"
                      />
                      <Typography variant="caption" sx={{ textTransform: 'none' }}>
                        {formatDistance(notification.timestamp, new Date(), {
                          addSuffix: true
                        })}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ 
                        fontWeight: notification.read ? 'normal' : 'medium',
                        wordBreak: 'break-word'
                      }}
                    >
                      {notification.message}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
            ))
          ) : (
            <ListItem sx={{ p: 2, minWidth: 350, textAlign: 'center' }}>
              <Box flex="1">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  No new notifications
                </Typography>
              </Box>
            </ListItem>
          )}
        </List>
      </Popover>
      ) : (
        <Dialog open={isOpen} onClose={handleClose} fullScreen>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
            Notifications
            <IconButton onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, pt: 1 }}>
              {unreadCount > 0 && (
                <Button size="small" variant="outlined" onClick={handleMarkAllAsRead} sx={{ textTransform: 'none' }}>
                  Mark all as read
                </Button>
              )}
            </Box>
            <Divider />
            <List sx={{ p: 0 }}>
              {firstLoad ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <ListItem key={i} sx={{ p: 2 }}>
                    <Box display="flex" alignItems="flex-start" gap={1} width="100%">
                      <Skeleton variant="circular" width={28} height={28} />
                      <Box flex={1}>
                        <Skeleton width="45%" />
                        <Skeleton width="95%" />
                      </Box>
                    </Box>
                  </ListItem>
                ))
              ) : notifications.length > 0 ? (
                notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{ p: 2, display: 'block', backgroundColor: notification.read ? 'transparent' : 'action.hover' }}
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      {getSeverityIcon(notification.severity)}
                      <Box flex="1">
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                          <Chip label={notification.severity.toUpperCase()} size="small" color={notification.severity === 'error' ? 'error' : 'warning'} variant="outlined" />
                          <Typography variant="caption" sx={{ textTransform: 'none' }}>
                            {formatDistance(notification.timestamp, new Date(), { addSuffix: true })}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.primary" sx={{ fontWeight: notification.read ? 'normal' : 'medium', wordBreak: 'break-word' }}>
                          {notification.message}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                ))
              ) : (
                <ListItem sx={{ p: 2, textAlign: 'center' }}>
                  <Box flex="1">
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No new notifications
                    </Typography>
                  </Box>
                </ListItem>
              )}
            </List>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default HeaderNotifications;
